import { ChevronDown, ChevronUp, RotateCcw, Search, Users } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

function SectionBar({
  children,
  collapsible,
  open,
  onToggle,
}: {
  children: React.ReactNode;
  collapsible?: boolean;
  open?: boolean;
  onToggle?: () => void;
}) {
  if (collapsible) {
    return (
      <button
        className="flex w-full items-center justify-between bg-gray-50 px-4 py-2.5 text-left"
        onClick={onToggle}
        type="button"
      >
        <span className="font-bold text-gray-700 text-[13px]">{children}</span>
        {open ? (
          <ChevronUp className="h-4 w-4 text-gray-400" />
        ) : (
          <ChevronDown className="h-4 w-4 text-gray-400" />
        )}
      </button>
    );
  }
  return (
    <div className="bg-gray-50 px-4 py-2.5">
      <span className="font-bold text-gray-700 text-[13px]">{children}</span>
    </div>
  );
}

function FormLabel({ children, htmlFor }: { children: React.ReactNode; htmlFor?: string }) {
  return (
    <label className="mb-1 block font-medium text-gray-600 text-xs" htmlFor={htmlFor}>
      {children}
    </label>
  );
}

function FormSelect({ label, options, id }: { label: string; options: string[]; id: string }) {
  return (
    <div>
      <FormLabel htmlFor={id}>{label}</FormLabel>
      <select
        className="w-full rounded border border-gray-300 bg-white px-2.5 py-1.5 text-sm focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500"
        id={id}
      >
        <option value="">選択してください</option>
        {options.map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
      </select>
    </div>
  );
}

function FormInput({
  label,
  placeholder,
  id,
}: {
  label: string;
  placeholder?: string;
  id: string;
}) {
  return (
    <div>
      <FormLabel htmlFor={id}>{label}</FormLabel>
      <input
        className="w-full rounded border border-gray-300 px-2.5 py-1.5 text-sm focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500"
        id={id}
        placeholder={placeholder}
        type="text"
      />
    </div>
  );
}

function FormRadioGroup({
  label,
  options,
  name,
}: {
  label: string;
  options: string[];
  name: string;
}) {
  return (
    <div>
      <FormLabel>{label}</FormLabel>
      <div className="flex flex-wrap gap-3">
        {options.map((opt, i) => (
          <label className="flex items-center gap-1 text-sm" key={opt}>
            <input
              className="accent-purple-600"
              defaultChecked={i === 0}
              name={name}
              type="radio"
              value={opt}
            />
            <span>{opt}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

function FormCheckboxGroup({ label, options }: { label: string; options: string[] }) {
  return (
    <div>
      <FormLabel>{label}</FormLabel>
      <div className="flex flex-wrap gap-3">
        {options.map((opt) => (
          <label className="flex items-center gap-1 text-sm" key={opt}>
            <input className="accent-purple-600" type="checkbox" value={opt} />
            <span>{opt}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

function FormRangeSelect({
  label,
  options,
  idFrom,
  idTo,
}: {
  label: string;
  options: string[];
  idFrom: string;
  idTo: string;
}) {
  return (
    <div>
      <FormLabel>{label}</FormLabel>
      <div className="flex items-center gap-1.5">
        <select
          className="flex-1 rounded border border-gray-300 bg-white px-2.5 py-1.5 text-sm focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500"
          id={idFrom}
        >
          <option value="">下限なし</option>
          {options.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
        <span className="text-gray-400 text-[13px]">〜</span>
        <select
          className="flex-1 rounded border border-gray-300 bg-white px-2.5 py-1.5 text-sm focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500"
          id={idTo}
        >
          <option value="">上限なし</option>
          {options.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}

function InputWithToggle({
  label,
  placeholder,
  id,
  toggleOptions,
}: {
  label: string;
  placeholder?: string;
  id: string;
  toggleOptions: string[];
}) {
  return (
    <div>
      <FormLabel htmlFor={id}>{label}</FormLabel>
      <div className="flex gap-1.5">
        <input
          className="flex-1 rounded border border-gray-300 px-2.5 py-1.5 text-sm focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500"
          id={id}
          placeholder={placeholder}
          type="text"
        />
        <select className="rounded border border-gray-300 bg-white px-1.5 py-1.5 text-xs">
          {toggleOptions.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}

export function SearchForm() {
  const navigate = useNavigate();
  const [showDetail, setShowDetail] = useState(true);
  const [showDesired, setShowDesired] = useState(true);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    navigate("/direct-recruiting-sample/results");
  };

  const ageOptions = Array.from({ length: 41 }, (_, i) => `${i + 20}歳`);
  const salaryOptions = [
    "200万円",
    "300万円",
    "400万円",
    "500万円",
    "600万円",
    "700万円",
    "800万円",
    "900万円",
    "1000万円",
    "1200万円",
    "1500万円",
    "2000万円",
  ];

  return (
    <form onSubmit={handleSubmit}>
      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
        {/* 基本条件 */}
        <SectionBar>基本条件</SectionBar>
        <div className="grid grid-cols-2 gap-x-6 gap-y-3 px-4 py-4">
          <FormSelect
            id="jobCategory"
            label="職種"
            options={[
              "ITエンジニア（システム開発・SE・インフラ）",
              "経営企画・事業開発",
              "マーケティング・広報",
              "営業・カスタマーサクセス",
              "人事・総務・法務",
              "金融・ファイナンス",
              "生産技術・製造・品質管理",
              "データサイエンティスト・AI",
              "コンサルタント",
              "デザイナー・クリエイティブ",
            ]}
          />
          <FormSelect
            id="industry"
            label="業種"
            options={[
              "IT・通信・インターネット",
              "コンサルティング",
              "金融・保険",
              "メーカー（自動車・輸送機器）",
              "メーカー（電機・電子）",
              "広告・メディア",
              "不動産・建設",
              "人材サービス",
              "小売・流通",
              "医療・ヘルスケア",
            ]}
          />
          <InputWithToggle
            id="keyword"
            label="キーワード"
            placeholder="例：プロジェクトマネジメント、DX推進"
            toggleOptions={["すべてを含む", "いずれかを含む"]}
          />
          <InputWithToggle
            id="company"
            label="在籍企業名"
            placeholder="例：トヨタ、ソニー"
            toggleOptions={["AND", "OR", "以外", "直近のみ"]}
          />
          <InputWithToggle
            id="position"
            label="役職・部署"
            placeholder="例：部長、マネージャー"
            toggleOptions={["AND", "OR", "直近のみ"]}
          />
          <FormRangeSelect idFrom="ageFrom" idTo="ageTo" label="年齢" options={ageOptions} />
          <FormCheckboxGroup label="性別" options={["男性", "女性", "未回答"]} />
        </div>

        {/* 学歴・語学 */}
        <SectionBar>学歴・語学</SectionBar>
        <div className="grid grid-cols-2 gap-x-6 gap-y-3 px-4 py-4">
          <FormSelect
            id="education"
            label="学歴"
            options={[
              "大学院（博士）以上",
              "大学院（修士）以上",
              "大学以上",
              "短大・高専以上",
              "高校以上",
            ]}
          />
          <InputWithToggle
            id="schoolName"
            label="学校名"
            placeholder="例：東京大学、慶應義塾大学"
            toggleOptions={["AND", "OR"]}
          />
          <FormSelect
            id="englishLevel"
            label="語学力 英語"
            options={[
              "ネイティブ",
              "ビジネス会話レベル",
              "日常会話レベル",
              "読み書きレベル",
              "なし",
            ]}
          />
          <div className="grid grid-cols-2 gap-2">
            <FormSelect
              id="otherLanguage"
              label="その他言語"
              options={["中国語", "韓国語", "フランス語", "ドイツ語", "スペイン語", "ポルトガル語"]}
            />
            <FormSelect
              id="otherLanguageLevel"
              label="レベル"
              options={["ネイティブ", "ビジネス会話", "日常会話", "読み書き"]}
            />
          </div>
        </div>

        {/* 詳細条件 */}
        <SectionBar collapsible onToggle={() => setShowDetail(!showDetail)} open={showDetail}>
          詳細条件
        </SectionBar>
        {showDetail ? (
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 px-4 py-4">
            <FormSelect
              id="lastLogin"
              label="最終ログイン"
              options={[
                "1日以内",
                "3日以内",
                "1週間以内",
                "2週間以内",
                "1ヶ月以内",
                "3ヶ月以内",
                "6ヶ月以内",
              ]}
            />
            <FormRadioGroup
              label="閲覧履歴"
              name="viewHistory"
              options={["問わない", "既読", "未読"]}
            />
            <FormRadioGroup
              label="職務経歴書の文字数"
              name="resumeLength"
              options={["問わない", "100字以上", "400字以上"]}
            />
            <FormSelect
              id="registeredCompanies"
              label="登録社数"
              options={["1社以下", "3社以下", "5社以下", "10社以下"]}
            />
            <FormRadioGroup
              label="直近転職者の除外"
              name="recentJobChange"
              options={["問わない", "半年以内を除外", "1年以内を除外"]}
            />
            <div>
              <FormCheckboxGroup label="職務経歴書" options={["和文あり", "英文あり"]} />
              <div className="mt-2">
                <FormSelect
                  id="resumeUpdate"
                  label="更新時期"
                  options={["1ヶ月以内", "3ヶ月以内", "6ヶ月以内", "1年以内"]}
                />
              </div>
            </div>
            <FormSelect
              id="managementExp"
              label="マネジメント経験"
              options={["1名以上", "5名以上", "10名以上", "30名以上", "50名以上", "100名以上"]}
            />
            <div>
              <FormLabel>直近の年収</FormLabel>
              <div className="flex items-center gap-1.5">
                <select className="flex-1 rounded border border-gray-300 bg-white px-2.5 py-1.5 text-sm focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500">
                  <option value="">下限なし</option>
                  {salaryOptions.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
                <span className="text-gray-400 text-[13px]">〜</span>
                <select className="flex-1 rounded border border-gray-300 bg-white px-2.5 py-1.5 text-sm focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500">
                  <option value="">上限なし</option>
                  {salaryOptions.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <FormCheckboxGroup label="会員種別" options={["ハイクラス", "タレント"]} />
            <FormInput id="memberId" label="会員ID" placeholder="例：BZ-2024-00001" />
            <FormRadioGroup
              label="就労状況"
              name="employmentStatus"
              options={["問わない", "現職", "離職・離職予定"]}
            />
            <FormSelect
              id="location"
              label="現住所"
              options={[
                "北海道",
                "東京都",
                "神奈川県",
                "千葉県",
                "埼玉県",
                "愛知県",
                "大阪府",
                "京都府",
                "兵庫県",
                "福岡県",
                "その他",
              ]}
            />
          </div>
        ) : null}

        {/* 希望条件 */}
        <SectionBar collapsible onToggle={() => setShowDesired(!showDesired)} open={showDesired}>
          希望条件
        </SectionBar>
        {showDesired ? (
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 px-4 py-4">
            <FormSelect
              id="desiredJobCategory"
              label="希望職種"
              options={[
                "ITエンジニア",
                "経営企画・事業開発",
                "マーケティング",
                "営業",
                "人事",
                "金融",
                "コンサルタント",
                "データサイエンティスト",
              ]}
            />
            <FormSelect
              id="desiredIndustry"
              label="希望業種"
              options={[
                "IT・通信・インターネット",
                "SaaS",
                "コンサルティング",
                "金融・保険",
                "メーカー",
                "広告・メディア",
              ]}
            />
            <div>
              <FormLabel>希望年収</FormLabel>
              <div className="flex items-center gap-1.5">
                <select className="flex-1 rounded border border-gray-300 bg-white px-2.5 py-1.5 text-sm focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500">
                  <option value="">指定なし</option>
                  {salaryOptions.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
                <select className="rounded border border-gray-300 bg-white px-1.5 py-1.5 text-xs">
                  <option>以上</option>
                  <option>未満</option>
                </select>
              </div>
            </div>
            <FormSelect
              id="desiredLocation"
              label="希望勤務地"
              options={[
                "北海道",
                "東京都",
                "神奈川県",
                "千葉県",
                "埼玉県",
                "愛知県",
                "大阪府",
                "京都府",
                "兵庫県",
                "福岡県",
                "リモート勤務可",
                "その他",
              ]}
            />
          </div>
        ) : null}
      </div>

      {/* 検索フッター */}
      <div className="sticky bottom-0 -mx-5 mt-0 flex items-center justify-between border-gray-200 border-t bg-white px-5 py-3 shadow-[0_-2px_4px_rgba(0,0,0,0.05)]">
        <button
          className="flex items-center gap-1.5 rounded border border-gray-300 px-3 py-2 text-gray-500 text-[13px] hover:bg-gray-50"
          type="reset"
        >
          <RotateCcw className="h-3.5 w-3.5" />
          条件をリセット
        </button>
        <div className="flex items-center gap-4">
          <span className="flex items-center gap-1.5 text-gray-500 text-[13px]">
            <Users className="h-3.5 w-3.5" />
            該当候補者: 約<span className="font-bold text-gray-800">1,247</span>名
          </span>
          <button
            className="flex items-center gap-1.5 rounded bg-purple-600 px-6 py-2 font-medium text-sm text-white shadow-sm hover:bg-purple-700"
            type="submit"
          >
            <Search className="h-3.5 w-3.5" />
            この条件で検索
          </button>
        </div>
      </div>
    </form>
  );
}
